// Shared AG Charts theme for the whole app - the ONE place to change cross-chart appearance.
//
// AG Charts is styled through this JS theme object, NOT through CSS. CSS only sizes/borders
// the container; everything visual inside the canvas (palette, line width, markers, legend,
// axis text) is driven from here. Every chart rendered through <CubeAgChart> picks this up, so
// editing this file updates every chart at once - that's the answer to "wrap vs CSS".
//
// Reuses the existing analytics palette so charts stay on-brand and we don't duplicate colours.
import type { AgChartTheme } from "ag-charts-community";
import {
  darkAnalyticFills,
  lightAnalyticFills,
} from "@/components/widgets/analytics/AnalyticsStyles";

// The chart surface each theme draws on: the widget background (cubeBase-500 / cubeBaseDark-500).
// Exported because a mark sitting on a line needs a ring in the surface colour to lift off it, and
// because the overlay palette's contrast was validated against these two values.
export const CUBE_CHART_SURFACE_LIGHT = "#FFFFFF";
export const CUBE_CHART_SURFACE_DARK = "#1B1919";

// Palette for REFERENCE MARKS drawn on top of a series - quoted prices, benchmarks, targets. The
// main palette above is tuned for area (pie/donut/sankey) and is too low-chroma and too close
// hue-to-hue to carry small marks: its slots sit at 1.4-1.7:1 contrast on the dark surface, under
// the 0.10 chroma floor (so they read as gray), and its worst normal-vision separation is dE 3.8
// light / 6.1 dark against a floor of 15. Series colours are untouched; this is additive.
//
// Validated all-pairs (marks can land anywhere relative to each other) in both modes:
//   node scripts/validate_palette.js "#2a78d6,#eda100,#e87ba4,#008300" --mode light --surface "#FFFFFF" --pairs all
//   node scripts/validate_palette.js "#3987e5,#c98500,#d55181,#008300" --mode dark  --surface "#1B1919" --pairs all
// Both pass: worst normal-vision dE 19.6 light / 19.3 dark. Every slot also clears the floor
// against the analytics slot-1 curve colour it sits on (worst dE 15.7 light / 25.6 dark).
// Two knock-on obligations:
//   - dark CVD dE 6.9 (protan) is in the 6-8 band, legal ONLY with secondary encoding - hence the
//     per-counterparty marker shapes and the always-on legend.
//   - light-mode yellow (2.17:1) and magenta (2.69:1) are under 3:1 on white, which obliges visible
//     labels - the legend names every series and every series carries a tooltip.
// FOUR IS THE CAP: no five-hue subset of the documented palette clears all-pairs in both modes.
// Adding a slot means re-running the validator, not eyeballing a hex.
export const CUBE_CHART_OVERLAY_PALETTE_LIGHT = [
  "#2a78d6", // blue
  "#eda100", // yellow
  "#e87ba4", // magenta
  "#008300", // green
];

export const CUBE_CHART_OVERLAY_PALETTE_DARK = [
  "#3987e5", // blue
  "#c98500", // yellow
  "#d55181", // magenta
  "#008300", // green
];

// Neutral ink for overlay marks past the palette cap. They keep their own shape, legend entry and
// tooltip (composite encoding), but share this colour rather than inventing a 5th hue.
export const CUBE_CHART_OVERFLOW_LIGHT = "#52514e";
export const CUBE_CHART_OVERFLOW_DARK = "#c3c2b7";

// The series-colour palette for the active theme. Series with no explicit fill/stroke draw
// from this in order (series[i] -> palette[i]). Exported so callers that need to colour their
// OWN UI to match a series (e.g. a combined tooltip's per-series dots) use the same source.
export const getCubeChartPalette = (theme?: string): string[] =>
  theme === "dark" ? darkAnalyticFills : lightAnalyticFills;

// The palette for reference marks layered over those series. Assigned in sequence, never cycled.
export const getCubeChartOverlayPalette = (theme?: string): string[] =>
  theme === "dark"
    ? CUBE_CHART_OVERLAY_PALETTE_DARK
    : CUBE_CHART_OVERLAY_PALETTE_LIGHT;

// The surface the chart is drawn on. Use it for the ring around overlapping marks.
export const getCubeChartSurface = (theme?: string): string =>
  theme === "dark" ? CUBE_CHART_SURFACE_DARK : CUBE_CHART_SURFACE_LIGHT;

// Colour for overlay marks beyond the overlay palette's cap.
export const getCubeChartOverflowColour = (theme?: string): string =>
  theme === "dark" ? CUBE_CHART_OVERFLOW_DARK : CUBE_CHART_OVERFLOW_LIGHT;

// Build the shared theme for the active next-themes value ("dark" | "light" | undefined).
export const getCubeChartTheme = (theme?: string): AgChartTheme => {
  const isDark = theme === "dark";
  const fills = getCubeChartPalette(theme);

  return {
    // AG's own dark/light base handles canvas text, gridlines, tooltips for the active mode.
    baseTheme: isDark ? "ag-default-dark" : "ag-default",
    // Series colours. A series with no explicit fill/stroke draws from this palette, so the
    // Swap Curve line is coloured here rather than hardcoded in the chart component.
    palette: {
      fills,
      strokes: fills,
    },
    overrides: {
      // Shared defaults applied to every chart type.
      common: {
        legend: { item: { label: { fontSize: 11 } } },
      },
      // Line-chart defaults (the Swap Curve, and any future line chart). Change once here.
      line: {
        series: {
          strokeWidth: 2,
          marker: { enabled: true, size: 4 },
        },
      },
    },
  };
};
